"use strict";
/**
 * Copyright © 2020 Johnson & Johnson
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 * http://www.apache.org/licenses/LICENSE-2.0
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

var __decorate = this && this.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var __metadata = this && this.__metadata || function (k, v) {
  if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};

var __spreadArrays = this && this.__spreadArrays || function () {
  for (var s = 0, i = 0, il = arguments.length; i < il; i++) {
    s += arguments[i].length;
  }

  for (var r = Array(s), k = 0, i = 0; i < il; i++) {
    for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++) {
      r[k] = a[j];
    }
  }

  return r;
};

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.BreadcrumbStore = exports.BreadcrumbItem = void 0;
/* eslint-disable no-underscore-dangle */

var mobx_1 = require("mobx");

var DEFAULT_URL_BASE = 'http://host';

var trimTrailingSlash = function trimTrailingSlash(str) {
  return str.replace(/\/$/, '');
};

var isChildOf = function isChildOf(child, parent) {
  if (child === parent) return false;
  var childTokens = child.split('/').filter(function (i) {
    return i.length;
  });
  var parentTokens = parent.split('/').filter(function (i) {
    return i.length;
  });
  return parentTokens.every(function (t, i) {
    return childTokens[i] === t;
  });
};
/**
 * Stores breadcrumb item data.
 */


var BreadcrumbItem =
/** @class */
function () {
  function BreadcrumbItem(_a) {
    var uuid = _a.uuid,
        title = _a.title,
        link = _a.link,
        store = _a.store,
        parent = _a.parent;
    this._uuid = uuid;
    this._parent = parent;
    this._title = title;
    this._link = link;
    this._store = store;
  }

  BreadcrumbItem.prototype.isActive = function () {
    var _this = this;

    return Boolean(this._store.breadcrumbTrail.find(function (tItem) {
      return tItem.isEqual(_this);
    }));
  };

  BreadcrumbItem.prototype.isSubpathOf = function (item) {
    var base = DEFAULT_URL_BASE;
    var itemURL = typeof item === 'string' ? new URL(item, base) : new URL(item.link.data, base);
    var thisURL = new URL(this.link.data, base);
    if (itemURL.host !== thisURL.host) return false;
    return isChildOf(itemURL.pathname, thisURL.pathname);
  };

  BreadcrumbItem.prototype.hasPath = function (item) {
    var base = DEFAULT_URL_BASE;
    var itemURL = typeof item === 'string' ? new URL(item, base) : new URL(item.link.data, base);
    var thisURL = new URL(this.link.data, base);
    if (itemURL.host !== thisURL.host) return false;
    return trimTrailingSlash(thisURL.pathname) === trimTrailingSlash(itemURL.pathname);
  };

  BreadcrumbItem.prototype.isDescendantOf = function (item) {
    for (var current = this._parent; current; current = this._parent) {
      if (current === item) return true;
    }

    return false;
  };

  BreadcrumbItem.prototype.isAncestorOf = function (item) {
    var isDescendant = item.isDescendantOf(this);
    return isDescendant;
  };

  BreadcrumbItem.prototype.isEqual = function (item) {
    var uuid = typeof item === 'string' ? item : item.uuid;
    return uuid === this._uuid;
  };

  BreadcrumbItem.prototype.isFirst = function () {
    return this.parent === undefined;
  };

  BreadcrumbItem.prototype.isLast = function () {
    var lastTrailItem = this._store.breadcrumbTrail[this._store.breadcrumbTrail.length - 1];
    return this._uuid === lastTrailItem.uuid;
  };

  BreadcrumbItem.prototype.getAncestors = function () {
    var ancestors = [];

    for (var current = this._parent; current; current = current.parent) {
      ancestors.push(current);
    }

    return ancestors;
  };

  Object.defineProperty(BreadcrumbItem.prototype, "uuid", {
    get: function get() {
      return this._uuid;
    },
    enumerable: false,
    configurable: true
  });
  Object.defineProperty(BreadcrumbItem.prototype, "title", {
    get: function get() {
      return this._title;
    },
    enumerable: false,
    configurable: true
  });
  Object.defineProperty(BreadcrumbItem.prototype, "link", {
    get: function get() {
      return this._link;
    },
    enumerable: false,
    configurable: true
  });
  Object.defineProperty(BreadcrumbItem.prototype, "parent", {
    get: function get() {
      return this._parent;
    },
    enumerable: false,
    configurable: true
  });

  BreadcrumbItem.prototype.setTitle = function (title) {
    this._title = title;
  };

  BreadcrumbItem.prototype.setLink = function (link) {
    this._link = link;
  };

  BreadcrumbItem.prototype.toString = function () {
    return "[" + this.link.data + (this.isActive() ? '***' : '') + "](" + this.uuid + ")";
  };

  return BreadcrumbItem;
}();

exports.BreadcrumbItem = BreadcrumbItem;
/**
 * MobX storage of breadcrumb items.
 * API:
 * + set/delete item.
 * + get breadcrumb trail.
 * + check if last breadcrumb item exists in the store.
 */

var BreadcrumbStore =
/** @class */
function () {
  function BreadcrumbStore(pagePath) {
    // eslint-disable-next-line max-len
    this.items = new Map();
    this.activeItem = undefined;
    this.pagePath = pagePath;
  }

  BreadcrumbStore.prototype.setActiveItem = function (item) {
    this.activeItem = item;
  };

  BreadcrumbStore.prototype.isNewActive = function (item) {
    return (item.hasPath(this.pagePath) || item.isSubpathOf(this.pagePath)) && (!this.activeItem || this.activeItem.isSubpathOf(item));
  };

  BreadcrumbStore.prototype.updateActive = function () {
    var _this = this;

    this.setActiveItem(undefined);
    this.items.forEach(function (item) {
      if (_this.isNewActive(item)) _this.setActiveItem(item);
    });
  };

  BreadcrumbStore.prototype.isActiveItemPathChanged = function (item) {
    return this.activeItem !== undefined && this.activeItem.isEqual(item) && !this.activeItem.hasPath(item);
  };

  BreadcrumbStore.prototype.getItem = function (key) {
    return this.items.get(key);
  };

  BreadcrumbStore.prototype.setItem = function (item) {
    this.items.set(item.uuid, item);
    if (this.isActiveItemPathChanged(item)) this.updateActive();
    if (this.isNewActive(item)) this.setActiveItem(item);
    return item;
  };

  BreadcrumbStore.prototype.deleteItem = function (item) {
    var uuid = typeof item === 'string' ? item : item.uuid;
    var result = this.items.delete(uuid);
    if (this.activeItem !== undefined && this.activeItem.isEqual(item)) this.updateActive();
    return result;
  };

  BreadcrumbStore.prototype.getPagePath = function () {
    return this.pagePath;
  };

  Object.defineProperty(BreadcrumbStore.prototype, "breadcrumbTrail", {
    get: function get() {
      if (this.activeItem === undefined) return [];
      return __spreadArrays([this.activeItem], this.activeItem.getAncestors()).reverse();
    },
    enumerable: false,
    configurable: true
  });

  BreadcrumbStore.prototype.export = function () {
    return Array.from(this.items.values());
  };

  BreadcrumbStore.prototype.hasCurrentPageItem = function () {
    return this.activeItem !== undefined && this.activeItem.hasPath(this.pagePath);
  };

  BreadcrumbStore.prototype.toString = function () {
    return this.breadcrumbTrail.map(function (i) {
      return i.toString();
    }).join('--');
  };

  __decorate([mobx_1.observable, __metadata("design:type", Map)], BreadcrumbStore.prototype, "items", void 0);

  __decorate([mobx_1.observable, __metadata("design:type", Object)], BreadcrumbStore.prototype, "activeItem", void 0);

  __decorate([mobx_1.action, __metadata("design:type", Function), __metadata("design:paramtypes", [Object]), __metadata("design:returntype", void 0)], BreadcrumbStore.prototype, "setActiveItem", null);

  __decorate([mobx_1.action, __metadata("design:type", Function), __metadata("design:paramtypes", [Object]), __metadata("design:returntype", void 0)], BreadcrumbStore.prototype, "setItem", null);

  __decorate([mobx_1.action, __metadata("design:type", Function), __metadata("design:paramtypes", [Object]), __metadata("design:returntype", void 0)], BreadcrumbStore.prototype, "deleteItem", null);

  __decorate([mobx_1.computed, __metadata("design:type", Object), __metadata("design:paramtypes", [])], BreadcrumbStore.prototype, "breadcrumbTrail", null);

  return BreadcrumbStore;
}();

exports.BreadcrumbStore = BreadcrumbStore;